/**
 * CompactMobileHeader.jsx
 * ============================================================================
 *  DESIGN SYSTEM · Gruppo Impresa Suite · v1.1.1
 * ============================================================================
 *
 * Barra sticky compatta per smartphone e tablet (<lg = 1024 px).
 * Standard di riferimento per TUTTE le applicazioni della Suite:
 * GI Presenze · GI Studio · GI Fatturazione · GI Finance · GI Connect · …
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  PRINCIPI ARCHITETTURALI (non negoziabili)
 * ────────────────────────────────────────────────────────────────────────────
 *  1. Barra SEMPRE VISIBILE quando l'utente è in modalità compatta. Nessun
 *     auto-collapse via scroll: prevedibilità > cleverness (Linear, Notion).
 *  2. Altezza fissa 48 px (target touch 44 px + padding), pollice-friendly.
 *  3. Contenuto MINIMO: contesto temporale/entita + azione primaria + accesso
 *     a Sheet Dettagli per tutto il resto.
 *  4. Nessuna gesture custom: solo tap. Zero swipe, zero long-press, zero
 *     shortcut a scoperta.
 *  5. Role-aware: l'app che integra il componente PUÒ omettere il selettore
 *     entità se il ruolo non lo utilizza (es. employee su GI Presenze).
 *  6. Nasconditore desktop: il componente NON si mostra automaticamente su
 *     ≥ lg. E' responsabilita' dell'integratore usarlo dentro un
 *     `<div className="lg:hidden">` o di combinarlo con `useIsCompact()`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  API (props)
 * ────────────────────────────────────────────────────────────────────────────
 *  context   {label, onClick, onPrev?, onNext?}      · dominio temporale/logico
 *  entity    {label, onClick} | null                 · null = ruolo senza cambio
 *  primary   {label, onClick, disabled, loading}     · azione principale
 *  secondary {icon?, onClick}                        · apre lo Sheet Dettagli
 *  testId    string                                  · prefisso data-testid
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ESEMPIO D'USO (GI Presenze)
 * ────────────────────────────────────────────────────────────────────────────
 *  import { CompactMobileHeader } from "@/components/design-system";
 *  import { useIsCompact } from "@/lib/useMediaQuery";
 *
 *  const isCompact = useIsCompact();
 *
 *  <div className="lg:hidden">
 *    <CompactMobileHeader
 *      context={{ label: "Ott. 26", onClick: openMonthPicker,
 *                 onPrev: prevMonth, onNext: nextMonth }}
 *      entity={role !== "employee"
 *        ? { label: "Rossi M.", onClick: openEmployeePicker }
 *        : null}
 *      primary={{ label: "Salva", onClick: saveAll,
 *                 disabled: !employeeId, loading: saving }}
 *      secondary={{ onClick: openDetails }}
 *      testId="attendance-mobile"
 *    />
 *  </div>
 *
 *  {isCompact && <Sheet .../> /* Sheet Dettagli e Sheet Entita' Picker *\/}
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ADOZIONE SUITE (roadmap)
 * ────────────────────────────────────────────────────────────────────────────
 *  · GI Presenze  · pagina Gestione Presenze (contesto = mese, entita = dip.)
 *  · GI Studio    · scheda pratica (contesto = data, entita = cliente)
 *  · GI Fatture   · elenco fatture (contesto = periodo, entita = cliente)
 *  · GI Finance   · movimenti (contesto = periodo, entita = conto)
 *  · GI Connect   · timeline notifiche (contesto = periodo, entita = null)
 * ============================================================================
 */
import React from "react";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";

export function CompactMobileHeader({
  context,
  entity = null,
  primary,
  secondary,
  testId = "compact-mobile-header",
}) {
  const SecondaryIcon = (secondary && secondary.icon) || SlidersHorizontal;
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-suite-neutral-200 bg-suite-surface shadow-sm text-xs"
      role="toolbar"
      aria-label="Barra strumenti compatta"
      data-testid={`${testId}-bar`}
    >
      {context.onPrev && (
        <button
          type="button"
          aria-label="Precedente"
          onClick={context.onPrev}
          className="h-8 w-7 grid place-items-center rounded-md border border-suite-neutral-200 bg-white active:bg-suite-neutral-100"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft size={15} />
        </button>
      )}

      <button
        type="button"
        aria-label={`Cambia ${context.ariaLabel || "contesto"}`}
        onClick={context.onClick}
        className={`min-w-[90px] h-8 px-2 rounded-md border border-suite-neutral-200 bg-white font-semibold text-suite-neutral-800 text-[13px] ${
          entity ? "" : "flex-1"
        }`}
        data-testid={`${testId}-context`}
      >
        {context.label}
      </button>

      {context.onNext && (
        <button
          type="button"
          aria-label="Successivo"
          onClick={context.onNext}
          className="h-8 w-7 grid place-items-center rounded-md border border-suite-neutral-200 bg-white active:bg-suite-neutral-100"
          data-testid={`${testId}-next`}
        >
          <ChevronRight size={15} />
        </button>
      )}

      {entity && (
        <button
          type="button"
          onClick={entity.onClick}
          className="flex-1 min-w-0 h-8 px-2 rounded-md border border-suite-neutral-200 bg-white text-left truncate text-[13px]"
          data-testid={`${testId}-entity`}
          title={entity.title || entity.label}
        >
          {entity.label}
        </button>
      )}

      {primary && (
        <button
          type="button"
          aria-label={primary.ariaLabel || primary.label}
          onClick={primary.onClick}
          disabled={primary.disabled || primary.loading}
          className="h-8 px-2.5 rounded-md bg-suite-primary text-white font-semibold text-[12px] disabled:opacity-50 active:bg-suite-primary/90 shrink-0"
          data-testid={`${testId}-primary`}
        >
          {primary.loading ? "…" : primary.label}
        </button>
      )}

      {secondary && (
        <button
          type="button"
          aria-label={secondary.ariaLabel || "Dettagli e strumenti"}
          onClick={secondary.onClick}
          className="h-8 w-8 grid place-items-center rounded-md border border-suite-neutral-200 bg-white shrink-0"
          data-testid={`${testId}-secondary`}
        >
          <SecondaryIcon size={15} />
        </button>
      )}
    </div>
  );
}

export default CompactMobileHeader;
